/**
 * Quick View — loads theme quickshop section (purchase essentials only).
 */
(function () {
  const SECTION_KEY = 'quickshop';

  const Quickshop = {
    cache: new Map(),
    pending: new Map(),
    assetPromise: null,
    _bound: false,

    init() {
      if (!this._bound) {
        document.addEventListener('click', (e) => this._onClick(e));
        document.addEventListener('pointerover', (e) => this._onIntent(e), { passive: true });
        document.addEventListener('focusin', (e) => this._onIntent(e));
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.close();
        });
        this._bound = true;
      }
    },

    _onClick(event) {
      if (event.target.closest('[data-quickview-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const trigger = event.target.closest('.product-listing__quickview-trigger.js-quickview-trigger');
      if (!trigger || trigger.closest('#predictiveSearch')) return;

      event.preventDefault();
      event.stopPropagation();
      this.open(trigger);
    },

    _onIntent(event) {
      const trigger = event.target.closest?.('.product-listing__quickview-trigger.js-quickview-trigger');
      if (!trigger || trigger.closest('#predictiveSearch')) return;
      this.prefetch(trigger);
    },

    prefetch(trigger) {
      const baseUrl = (trigger.dataset.productUrl || '').split('?')[0].split('#')[0];
      if (!baseUrl) return;

      this.fetchProductHtml(baseUrl).catch(() => {});
      this.preloadCoreAssets().catch(() => {});
    },

    getModal() {
      return document.getElementById('modal-quickview');
    },

    getContentEl() {
      return document.querySelector('#modal-quickview .js-quickview-content');
    },

    open(trigger) {
      const modal = this.getModal();
      const content = this.getContentEl();
      const baseUrl = (trigger.dataset.productUrl || '').split('?')[0].split('#')[0];

      if (!modal || !content) {
        console.error('[Quick View] #modal-quickview missing');
        return;
      }

      if (!baseUrl) {
        console.error('[Quick View] missing product URL');
        return;
      }

      trigger.classList.add('loading-quickshop');
      content.innerHTML = '<div class="quickview-modal__loading" aria-live="polite">Loading…</div>';
      this.show(modal);
      this.loadProduct(baseUrl, content, trigger);
    },

    async loadProduct(baseUrl, content, trigger) {
      try {
        const html = await this.fetchProductHtml(baseUrl);
        if (!html || !html.trim()) {
          throw new Error('Empty HTML from quickshop section');
        }

        content.innerHTML = html;

        const root = content.querySelector('[data-section-type="product"]');
        if (!root) {
          throw new Error('quickshop markup missing [data-section-type="product"]');
        }

        this.makeVisible(root);
        this.patchProductForm(root);
        await this.loadAssets(trigger, root);

        const mainSlider = root.querySelector('.js-carousel-main');
        const alreadyInit = mainSlider?.classList.contains('flickity-enabled');
        if (!alreadyInit && typeof Product === 'function') {
          try {
            Product(root);
          } catch (productErr) {
            console.warn('[Quick View] Product():', productErr);
          }
        }

        if (root.querySelector('[data-product-form]') && window.Shopify?.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        const submit = root.querySelector('.js-ajax-submit');
        if (submit && window.WAU?.AjaxCart) {
          submit.addEventListener('click', (e) => {
            e.preventDefault();
            const form = submit.closest('form');
            const cartConfig = document.getElementById('cart-config');
            if (!form || !cartConfig) return;
            WAU.AjaxCart.addToCart(form, null, JSON.parse(cartConfig.innerHTML || '{}'), true, false);
          });
        }
      } catch (error) {
        console.error('[Quick View]', error);
        const fallback = (trigger.dataset.productUrl || '').split('?')[0];
        content.innerHTML =
          '<p class="quickview-modal__error">Unable to load product.' +
          (fallback ? ' <a href="' + fallback + '">View product page</a>' : '') +
          '</p>';
      } finally {
        trigger.classList.remove('loading-quickshop');
      }
    },

    async fetchProductHtml(baseUrl) {
      const url = baseUrl + '?section_id=' + encodeURIComponent(SECTION_KEY);
      const cached = this.cache.get(url);
      if (cached) return cached;
      const pending = this.pending.get(url);
      if (pending) return pending;

      const request = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('section_id ' + response.status);
          return response.text();
        })
        .then((text) => {
          const html = this.parseSectionHtml(text);
          if (!html) throw new Error('quickshop section parse failed');
          this.cache.set(url, html);
          return html;
        })
        .finally(() => this.pending.delete(url));

      this.pending.set(url, request);
      return request;
    },

    parseSectionHtml(htmlText) {
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');
      const productRoot = doc.querySelector('[data-section-type="product"]');
      return productRoot ? productRoot.outerHTML : null;
    },

    makeVisible(context) {
      const root = context.closest('[data-section-type="product"]') || context;
      const content = this.getContentEl();
      const modal = this.getModal();

      root.classList.add('no-section-animation', 'is-quickview');
      root.setAttribute('data-section-loaded', 'true');

      if (content) content.classList.add('is-quickview');
      if (modal) modal.classList.add('no-section-animation');

      root.querySelectorAll('.animate--text').forEach((el) => el.classList.remove('animate--text'));
    },

    patchProductForm(context) {
      const form = context.querySelector('[data-product-form]');
      if (!form) return;
      try {
        const cfg = JSON.parse(form.getAttribute('data-product-form') || '{}');
        cfg.enable_history = false;
        cfg.quickview = true;
        form.setAttribute('data-product-form', JSON.stringify(cfg));
      } catch (e) {
        /* keep server config */
      }
    },

    async loadAssets(trigger, context) {
      if (typeof jsAssets === 'undefined' || !window.WAU?.Helpers?.scriptLoader) return;

      await this.preloadCoreAssets();

      const loader = new WAU.Helpers.scriptLoader();
      const followups = [];
      if (trigger.dataset.hasModel === 'true') followups.push(jsAssets.productModel);
      if (trigger.dataset.hasVideo === 'true') followups.push(jsAssets.productVideo);

      const sectionAsset = context?.dataset?.assetUrl;
      if (sectionAsset) followups.push(sectionAsset);

      await loader.load(followups.filter(Boolean));
    },

    preloadCoreAssets() {
      if (this.assetPromise) return this.assetPromise;
      if (typeof jsAssets === 'undefined' || !window.WAU?.Helpers?.scriptLoader) return Promise.resolve();

      const assets = [jsAssets.flickity, jsAssets.product].filter(Boolean);
      if (!assets.length) return Promise.resolve();

      const loader = new WAU.Helpers.scriptLoader();
      this.assetPromise = loader.load(assets).catch((error) => {
        this.assetPromise = null;
        throw error;
      });
      return this.assetPromise;
    },

    show(modal) {
      modal.removeAttribute('hidden');
      modal.classList.add('is-open', 'no-section-animation');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('quickview-modal-open');
    },

    close() {
      const modal = this.getModal();
      const content = this.getContentEl();
      if (!modal) return;

      modal.classList.remove('is-open', 'no-section-animation');
      modal.setAttribute('hidden', '');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('quickview-modal-open');
      if (content) {
        content.classList.remove('is-quickview');
        content.innerHTML = '';
      }
    },

    showModal() {
      const modal = this.getModal();
      if (modal) this.show(modal);
    },

    hideModal() {
      this.close();
    },

    showDrawer(trigger) {
      this.showModal();
      if (trigger) trigger.setAttribute('data-wau-slideout-target', 'quickview');
    },

    hideDrawer() {
      this.close();
    },

    getOnboard() {
      const onboard = document.querySelector('.js-quickview-onboard [data-section-type="product"]');
      const content = this.getContentEl();
      if (!onboard || !content) return;
      content.innerHTML = onboard.outerHTML;
      const root = content.querySelector('[data-section-type="product"]');
      if (root) this.makeVisible(root);
      const modal = this.getModal();
      if (modal) this.show(modal);
    },
  };

  window.WAU = window.WAU || {};
  WAU.Quickshop = Quickshop;

  function boot() {
    WAU.Quickshop.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.id === 'shopify-section-quickshop') WAU.Quickshop.getOnboard();
  });
})();

if (!customElements.get('wau-quick-add')) {
  customElements.define(
    'wau-quick-add',
    class extends HTMLElement {
      connectedCallback() {
        this.querySelector('details')?.addEventListener('toggle', async ({ target }) => {
          if (!target.open || typeof WAU.Helpers.scriptLoader !== 'function') return;
          const loader = new WAU.Helpers.scriptLoader();
          try {
            await loader.load([jsAssets.product]);
            ProductQuickForm(target);
          } catch (error) {
            console.error(error);
          }
        });
      }
    }
  );
}
